/** Number formatting. Every numeral renders tabular — see styles/base.css. */

export const money0 = (v: number | null) =>
  v === null ? '—' : v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

export const money2 = (v: number | null) =>
  v === null ? '—' : v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Compact dollars: $13.8M, $8.65M, $956K, $4.2K. */
export const moneyK = (v: number | null) => {
  if (v === null || !Number.isFinite(v)) return '—'
  const a = Math.abs(v)
  if (a >= 1e6) return `$${(v / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`
  if (a >= 1e3) return `$${(v / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K`
  return `$${v.toFixed(0)}`
}

export const int = (v: number | null) =>
  v === null ? '—' : Math.round(v).toLocaleString('en-US')

/** Percentages are NOT clamped — closed claim % legitimately exceeds 100%. */
export const pct = (v: number | null, dp = 1) =>
  v === null ? '—' : `${(v * 100).toFixed(dp)}%`

export const dec = (v: number | null, dp = 2) => (v === null ? '—' : v.toFixed(dp))

/** Hours: "1,112", "12.5", "0". One decimal at most — payroll spreads hours across days. */
export const hours = (v: number | null) =>
  v === null ? '—' : v.toLocaleString('en-US', { maximumFractionDigits: 1 })

/** 'YYYY-MM-DD' → "Jun 1, 2026". Read as a calendar date, never shifted by time zone. */
export const dateLong = (d: string | null) => {
  if (!d) return '—'
  const [y, m, day] = d.slice(0, 10).split('-').map(Number)
  if (!y || !m || !day) return d
  return new Date(y, m - 1, day).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** 'YYYY-MM-DD' → "8/14/2026", as Claim detail writes dates. Read as a calendar date. */
export const dateUs = (d: string | null) =>
  d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${+d.slice(5, 7)}/${+d.slice(8, 10)}/${d.slice(0, 4)}` : (d ?? '—')

/** "no cohort" — never a rank computed off fewer than 5 peers. */
export const rank = (r: { rank: number; of: number } | null) =>
  r === null ? 'no cohort' : `${r.rank} of ${r.of}`

/** 'YYYY-MM' → "Mar 2026". */
export const monthYear = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short', year: 'numeric' })

/** A run of 'YYYY-MM' keys as "Mar–May 2026", "Nov 2025–Feb 2026" or "Jun 2026". */
export const monthSpan = (months: readonly string[]): string => {
  const a = months[0]
  const b = months[months.length - 1]
  if (!a || !b) return 'No months'
  if (a === b) return monthYear(a)
  if (a.slice(0, 4) === b.slice(0, 4)) return `${monthYear(a).split(' ')[0]}–${monthYear(b)}`
  return `${monthYear(a)}–${monthYear(b)}`
}

/** The month after a 'YYYY-MM' key. */
const nextMonth = (m: string) => {
  const y = +m.slice(0, 4)
  const mo = +m.slice(5, 7)
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`
}

/**
 * Any set of 'YYYY-MM' keys, gaps and all (review #696): each unbroken run as monthSpan
 * reads it, "Mar 2026, May–Jun 2026", or "Mar, May–Jun 2026" when they share a year. One
 * run reads exactly as monthSpan does.
 */
export const monthRuns = (months: readonly string[]): string => {
  const sorted = [...new Set(months)].sort()
  const runs: string[][] = []
  for (const m of sorted) {
    const run = runs[runs.length - 1]
    if (run && nextMonth(run[run.length - 1]!) === m) run.push(m)
    else runs.push([m])
  }
  if (runs.length <= 1) return monthSpan(sorted)
  const year = sorted[0]!.slice(0, 4)
  if (sorted.every((m) => m.slice(0, 4) === year))
    return `${runs.map((r) => monthSpan(r).replace(` ${year}`, '')).join(', ')} ${year}`
  return runs.map(monthSpan).join(', ')
}
